import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@design-system/shadcn";
import { cn } from "@design-system/utils";
import { flexRender } from "@tanstack/react-table";
import React, { useRef } from "react";

import { useDataTableContext, useGridCellNavigation } from "../hooks";

const DEFAULT_COLUMN_SIZE = 150;

function getAriaSort(
  canSort: boolean,
  isSorted: false | "asc" | "desc"
): "ascending" | "descending" | "none" | undefined {
  if (!canSort) return undefined;
  if (isSorted === "asc") return "ascending";
  if (isSorted === "desc") return "descending";
  return "none";
}

function getCellProps(align?: "left" | "center" | "right", size?: number) {
  return {
    className: cn(
      align === "right" && "text-right",
      align === "center" && "text-center"
    ),
    style: size !== DEFAULT_COLUMN_SIZE ? { width: size } : undefined,
  };
}

interface TableViewProps {
  /**
   * Opt in to the WAI-ARIA data grid keyboard model: cell-level roving
   * tabindex (single tab stop), 2D arrow navigation, and focus-driven row
   * selection. Off by default so tables that don't need it are unaffected.
   */
  cellNavigation?: boolean;
}

function getRowTabIndex(
  row: { getIsSelected: () => boolean },
  index: number,
  rowSelection: Record<string, unknown>
): number {
  if (row.getIsSelected()) return 0;
  if (index === 0 && Object.keys(rowSelection).length === 0) return 0;
  return -1;
}

function getRowClassName(cellNavigation: boolean, clickable: boolean) {
  return cn(
    clickable &&
      "focus-visible:ring-ring-default focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-1 focus-visible:ring-inset",
    cellNavigation &&
      "[&_td]:focus-visible:ring-ring-default [&_td]:focus:outline-none [&_td]:focus-visible:ring-1 [&_td]:focus-visible:ring-inset",
    clickable &&
      "hover:bg-shape-neutral-outlined-background-hover cursor-pointer"
  );
}

function TableView({ cellNavigation = false }: Readonly<TableViewProps>) {
  const { table, onRowClick, onRowFocus, onEscape, search, meta } =
    useDataTableContext<unknown, unknown>();

  const containerRef = useRef<HTMLDivElement | null>(null);
  useGridCellNavigation({
    containerRef,
    enabled: cellNavigation,
    onEscape,
  });

  const pageOffset = (search.page - 1) * search.pageSize;
  const headerGroups = table.getHeaderGroups();

  const grid = (
    <Table
      className="my-4"
      role="grid"
      aria-rowcount={
        meta?.totalCount != null ? meta.totalCount + headerGroups.length : -1
      }
      aria-colcount={table.getVisibleLeafColumns().length}
    >
      <TableHeader>
        {headerGroups.map((headerGroup, headerIndex) => (
          <TableRow
            key={headerGroup.id}
            aria-rowindex={headerIndex + 1}
            onFocus={cellNavigation ? () => onRowFocus?.(null) : undefined}
          >
            {headerGroup.headers.map((header) => {
              const cellProps = getCellProps(
                header.column.columnDef.meta?.align,
                header.getSize()
              );

              const ariaSort = getAriaSort(
                header.column.getCanSort(),
                header.column.getIsSorted()
              );

              return (
                <TableHead key={header.id} aria-sort={ariaSort} {...cellProps}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row, index) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              aria-rowindex={pageOffset + index + headerGroups.length + 1}
              aria-selected={row.getIsSelected() || undefined}
              // In cell-navigation mode the roving tabindex lives on the cells
              // (managed by useGridCellNavigation), so the row itself is never
              // a tab stop. For clickable rows, use roving tabindex so keyboard
              // users can reach and activate them; non-clickable rows need no tab stop.
              tabIndex={
                cellNavigation || !onRowClick
                  ? undefined
                  : getRowTabIndex(row, index, table.getState().rowSelection)
              }
              onClick={() => onRowClick?.(row.original)}
              onKeyDown={
                onRowClick && !cellNavigation
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row.original);
                      }
                    }
                  : undefined
              }
              onFocus={
                cellNavigation ? () => onRowFocus?.(row.original) : undefined
              }
              className={getRowClassName(cellNavigation, !!onRowClick)}
            >
              {row.getVisibleCells().map((cell) => {
                const cellProps = getCellProps(
                  cell.column.columnDef.meta?.align,
                  cell.column.getSize()
                );

                return (
                  <TableCell key={cell.id} role="gridcell" {...cellProps}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={table.getAllColumns().length}
              className="h-24 text-center"
            >
              {/* empty by design; host controls empty state text */}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  // The cell-navigation hook resolves the grid via this container ref.
  return cellNavigation ? <div ref={containerRef}>{grid}</div> : grid;
}

export { TableView as Table };
export type { TableViewProps };
